import React, { useCallback, useRef, useState } from 'react';

// Generic "hold to confirm" button. Prevents accidental taps by requiring
// the user to press and hold for `duration` ms before firing onComplete.
export const HoldButton = ({
  onComplete,
  duration = 2000,
  Icon,
  idleLabel,
  holdingLabel,
  doneLabel,
  testid = 'hold-button',
  color = 'var(--success)',
  fill = 'var(--success)',
}) => {
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const rafRef = useRef(null);
  const startRef = useRef(0);

  const stop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setProgress((p) => (done ? p : 0));
  }, [done]);

  const tick = useCallback(() => {
    const elapsed = Date.now() - startRef.current;
    const p = Math.min(1, elapsed / duration);
    setProgress(p);
    if (p >= 1) {
      setDone(true);
      if (navigator.vibrate) navigator.vibrate([60, 30, 60]);
      onComplete && onComplete();
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [duration, onComplete]);

  const start = useCallback((e) => {
    e.preventDefault();
    if (done) return;
    startRef.current = Date.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [tick, done]);

  return (
    <button
      data-testid={testid}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchEnd={stop}
      className="relative w-full overflow-hidden rounded-2xl min-h-[56px] font-bold text-base select-none"
      style={{ background: 'var(--surface)', color, border: `2px solid ${color}` }}
    >
      <span
        className="absolute inset-y-0 left-0"
        style={{ width: `${progress * 100}%`, background: fill, transition: 'width 0.05s linear', opacity: 0.18 }}
      />
      <span className="relative flex items-center justify-center gap-2">
        {Icon && <Icon size={20} />}
        {done ? doneLabel : progress > 0 ? holdingLabel : idleLabel}
      </span>
    </button>
  );
};
